// Copyright (c) 2017 Uber Technologies, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
'use strict';

var CONSTANT = require('./constant');
var VALIDATOR_MAP = require('./validator-map');
var Utils = require('./utils');

var NUMBER_OF_ALLOWED_HITS = 3;

var Analyzer = {};

Analyzer._category = function _category(colType) {
  return CONSTANT.TYPES_TO_CATEGORIES[colType] || CONSTANT.CATEGORIES.DIMENSION;
};

var VALIDATOR_CONSIDERS_EMPTY_STRING_NULL = {
  PAIR_GEOMETRY_FROM_STRING: true,
  GEOMETRY_FROM_STRING: true,
  NUMBER: true
};

var VALIDATOR_CONSIDERS_NAN_NULL = {
  INT: true,
  NUMBER: true,
  FLOAT: true
};

/**
 * Check if a given value is a null for a validator
 * @param {String} value - value to be checked if null
 * @param {String} validatorName - the name of the current validation function
 * @return {Boolean} whether or not the current value is null
 **/
function valueIsNullForValidator(value, validatorName) {
  if (
    value === null ||
    value === CONSTANT.NULL ||
    value === CONSTANT.DB_NULL ||
    typeof value === 'undefined'
  ) {
    return true;
  }

  if (Number.isNaN(value) && VALIDATOR_CONSIDERS_NAN_NULL[validatorName]) {
    return true;
  }

  if (value === '' && VALIDATOR_CONSIDERS_EMPTY_STRING_NULL[validatorName]) {
    return true;
  }

  return false;
}

function buildValidatorFinder(data, columnName) {
  return function findTypeFromValidators(validatorName) {
    // you get three strikes until we dont think you are this type
    var nonNullData = data.filter(function iterator(row) {
      var value = row[columnName];
      return !valueIsNullForValidator(value, validatorName);
    });

    var validator = VALIDATOR_MAP[validatorName];

    var strikes = Math.min(NUMBER_OF_ALLOWED_HITS, nonNullData.length);
    var hits = 0;
    nonNullData.some(function iterateAcrossData(row) {
      var isValueValid = Boolean(validator(row[columnName]));
      if (isValueValid) {
        hits++;
      } else {
        strikes--;
      }

      if (strikes <= 0) {
        return true;
      }
      return false;
    });

    return strikes > 0 && hits > 0;
  };
}

function getTypeFromRules(analyzerRules, columnName) {
  return (analyzerRules || []).reduce(function checkClmns(newType, rule) {
    if (newType) {
      return newType;
    }
    if (rule.name && rule.name === columnName) {
      return rule.dataType;
    }
    if (rule.regex && rule.regex.test(columnName)) {
      return rule.dataType;
    }
    return newType;
  }, false);
}

function _pushIntoArr(arr, item) {
  arr.push(item);
}

function _noop() {}

/**
 * Generate metadata about columns in a dataset
 * @param {Object} data - data for which meta will be generated
 * @param {Object} analyzerRules - regexs describing column overrides
 * @param {Object.array} ignoredDataTypes - array of datatypes to ignore when validating
 * @return {Object} column metadata
 **/
Analyzer.computeColMeta = function computeColMeta(
  data,
  analyzerRules,
  options
) {
  options = options || {};
  var ignoredDataTypes = options.ignoredDataTypes || [];
  var keepUnknowns = options.keepUnknowns;
  var maybePushUnknown = keepUnknowns ? _pushIntoArr : _noop;
  var allValidators = CONSTANT.VALIDATORS.filter(function filterValidators(
    validator
  ) {
    return this.indexOf(validator) < 0;
  },
  ignoredDataTypes);

  if (!data || Object.keys(data).length === 0) {
    return [];
  }

  var _columns = Object.keys(data[0]);
  /* eslint-disable max-statements, complexity */
  return _columns.reduce(function iterator(res, columnName) {
    var format = '';
    // First try to get the column from the rules
    var type = getTypeFromRules(analyzerRules, columnName);
    // ff it's not there then try to infer the type
    if (!type) {
      type = allValidators.find(buildValidatorFinder(data, columnName));
    }
    var category = Analyzer._category(type);
    var colMeta = {
      key: columnName,
      label: columnName,
      type: CONSTANT.DATA_TYPES.STRING,
      category: category || CONSTANT.CATEGORIES.DIMENSION,
      format: ''
    };

    // if theres still no type, potentially dump this column
    if (!type) {
      maybePushUnknown(res, colMeta);
      return res;
    }
    colMeta.type = type;

    // if its a time, detect and record the time
    if (type && CONSTANT.TIME_VALIDATORS.indexOf(type) !== -1) {
      // Find the first non-null value.
      var sample = Utils.findFirstNonNullValue(data, columnName);
      if (sample === null) {
        maybePushUnknown(res, colMeta);
        return res;
      }
      format = Utils.detectTimeFormat(sample, type);
    }
    colMeta.format = format;

    if (type === CONSTANT.DATA_TYPES.GEOMETRY) {
      var geoSample = Utils.findFirstNonNullValue(data, columnName);
      if (geoSample === null) {
        maybePushUnknown(res, colMeta);
        return res;
      }
      colMeta.geoType =
        typeof geoSample.type === 'string'
          ? geoSample.type.toUpperCase()
          : null;
    }
    if (type === CONSTANT.DATA_TYPES.GEOMETRY_FROM_STRING) {
      var geoStringSample = Utils.findFirstNonNullValue(data, columnName);
      if (geoStringSample === null) {
        maybePushUnknown(res, colMeta);
        return res;
      }
      colMeta.geoType = geoStringSample.split(' ')[0].toUpperCase();
    }
    if (type === CONSTANT.DATA_TYPES.PAIR_GEOMETRY_FROM_STRING) {
      colMeta.geoType = 'POINT';
    }
    res.push(colMeta);
    return res;
  }, []);
};
/* eslint-enable max-statements */

module.exports = Analyzer;
